/**
 * ZoneChecklist - STEP 3 of the SVM checklist flow: the live zone view for one
 * project + one discipline (book).
 *
 * Owns the template load (GET /api/svm/template, module-level cache, fetched
 * ONCE per discipline), the zone scope, the checks Map with its optimistic-tick
 * + pendingKeys merge, the ~12s poll plus focus / visibilitychange refetch, the
 * header (breadcrumb, overall progress, robot filter, Edit zones, Synced-ago),
 * the zone chips, the phase → subsection → item tree and the failure toast.
 * Renders CellPopover and ZoneScopePanel.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { SVM_CHECK_URL, SVM_ZONES_URL, cachedTemplate, getJson, loadTemplate, sendJson, svmChecksUrl, svmHistoryUrl } from './svmApi';
import {
  BTN,
  BTN_GHOST,
  CARD,
  DEFAULT_DISCIPLINE,
  GREEN,
  INPUT,
  colsOf,
  computeActiveStats,
  computeModSiteStats,
  computeOverall,
  computeZoneStats,
  deriveScopedZones,
  keyOf,
  mergeServerChecks,
  nextZoneScope,
  normalizeScope,
  pctOf,
  safeRobotFilter,
  scopeHasKey,
  useIsNarrow,
} from './svmModel';
import ProductDataPanel from './ProductDataPanel';
import { Breadcrumb, ErrorBanner, LoadingNote, ProgressBar, SyncedAgo, Toast } from './SvmShared';
import SubsectionBlock from './SubsectionBlock';
import CellPopover from './CellPopover';
import ZoneScopePanel from './ZoneScopePanel';

export default function ZoneChecklist({ project, discipline = DEFAULT_DISCIPLINE, initialZones, onBackToProjects, onBackToDisciplines }) {
  const narrow = useIsNarrow();
  const disciplineId = discipline.id;
  const [template, setTemplate] = useState(() => cachedTemplate(disciplineId));
  const [tplError, setTplError] = useState(null);
  // Zone scope: array of { key, robots } or null = all zones at full robots.
  // Seeded from /disciplines, refreshed by every /checks response (colleague
  // edits ride the poll).
  const [zoneScope, setZoneScope] = useState(() => normalizeScope(initialZones));
  const [activeZone, setActiveZone] = useState(() => {
    const cached = cachedTemplate(disciplineId);
    if (!cached) return null;
    const scope = normalizeScope(initialZones);
    const first = cached.zones.find((z) => scopeHasKey(scope, z.key)) || cached.zones[0];
    return first ? first.key : null;
  });
  const [checks, setChecks] = useState(() => new Map());
  const [loadedZones, setLoadedZones] = useState(() => new Set());
  const [allLoaded, setAllLoaded] = useState(false);
  const [lastSync, setLastSync] = useState(null);
  const [checksError, setChecksError] = useState(null);
  const [collapsedSubs, setCollapsedSubs] = useState(() => new Set());
  const [robotFilter, setRobotFilter] = useState('ALL');
  const [pop, setPop] = useState(null);
  const [scopePanel, setScopePanel] = useState(null);
  const [toast, setToast] = useState(null);

  const pendingRef = useRef(new Set());
  const checksRef = useRef(checks);
  const toastTimer = useRef(null);
  const projectLabel = project.projectLabel;

  const showToast = useCallback((text) => {
    setToast(text);
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 4000);
  }, []);
  useEffect(
    () => () => {
      if (toastTimer.current) clearTimeout(toastTimer.current);
    },
    []
  );

  // ── template: fetched once per discipline, module-level cache ──
  const fetchTemplate = useCallback(() => {
    setTplError(null);
    loadTemplate(disciplineId)
      .then((tpl) => {
        setTemplate(tpl);
        setActiveZone((z) => {
          if (z) return z;
          const scope = normalizeScope(initialZones);
          const first = tpl.zones.find((zz) => scopeHasKey(scope, zz.key)) || tpl.zones[0];
          return first ? first.key : null;
        });
      })
      .catch((err) => setTplError(err.message || 'Failed to load the checklist template'));
  }, [disciplineId, initialZones]);
  useEffect(() => {
    if (!template) fetchTemplate();
  }, [template, fetchTemplate]);

  const mutateChecks = useCallback((fn) => {
    setChecks((prev) => {
      const next = new Map(prev);
      fn(next);
      checksRef.current = next;
      return next;
    });
  }, []);

  // Merge server state for one zone: server wins EXCEPT in-flight optimistic keys.
  const applyServer = useCallback((zone, serverChecks) => {
    setChecks((prev) => {
      const next = mergeServerChecks(prev, zone, serverChecks, pendingRef.current);
      if (next !== prev) checksRef.current = next;
      return next;
    });
    setLoadedZones((prev) => (prev.has(zone) ? prev : new Set(prev).add(zone)));
  }, []);

  const checksUrl = useCallback((zone) => svmChecksUrl(projectLabel, disciplineId, zone), [projectLabel]);

  // Merge a zones field from any server response. Identity-stable when the
  // scope (key set + robot counts) is unchanged so poll ticks don't re-render
  // or refire fetch effects.
  const applyScope = useCallback((zones) => {
    setZoneScope((prev) => nextZoneScope(prev, zones));
  }, []);

  // EFFECTIVE zones: template zones in this project's scope (template order),
  // each with its robots array sliced to the scoped count. Everything in the
  // zone view (grid, chips, totals, filter, prefetch) runs off these.
  // null scope = all template zones at full robots.
  const scopedZones = useMemo(() => deriveScopedZones(template, zoneScope), [template, zoneScope]);

  // If the scope was edited and the active zone fell out of it, jump to the
  // first in-scope zone.
  useEffect(() => {
    if (!activeZone || scopeHasKey(zoneScope, activeZone)) return;
    const first = scopedZones[0];
    if (first) setActiveZone(first.key);
  }, [zoneScope, activeZone, scopedZones]);

  // ── initial background load of in-scope zones (accurate chips + overall bar) ──
  useEffect(() => {
    if (scopedZones.length === 0) return undefined;
    let alive = true;
    Promise.allSettled(scopedZones.map((z) => getJson(checksUrl(z.key)))).then((results) => {
      if (!alive) return;
      results.forEach((r, i) => {
        if (r.status === 'fulfilled') {
          applyServer(scopedZones[i].key, r.value.checks || []);
          if (r.value.zones !== undefined) applyScope(r.value.zones);
        }
      });
      setAllLoaded(true);
    });
    return () => {
      alive = false;
    };
  }, [scopedZones, checksUrl, applyServer, applyScope]);

  // ── active-zone polling (~12s) + focus/visibility refetch ──
  useEffect(() => {
    if (!activeZone) return undefined;
    let alive = true;
    const load = async () => {
      try {
        const d = await getJson(checksUrl(activeZone));
        if (!alive) return;
        applyServer(activeZone, d.checks || []);
        if (d.zones !== undefined) applyScope(d.zones);
        setLastSync(Date.now());
        setChecksError(null);
      } catch (err) {
        if (alive) setChecksError(err.message || 'Sync failed');
      }
    };
    load();
    const iv = setInterval(load, 12000);
    const onFocus = () => load();
    const onVis = () => {
      if (document.visibilityState === 'visible') load();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVis);
    return () => {
      alive = false;
      clearInterval(iv);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [activeZone, checksUrl, applyServer, applyScope]);

  // ── write actions ──
  const tickDone = useCallback(
    async (zone, item, col) => {
      const k = keyOf(zone, item.no, col);
      if (pendingRef.current.has(k)) return;
      pendingRef.current.add(k);
      mutateChecks((m) =>
        m.set(k, {
          zone,
          itemNo: item.no,
          col,
          status: 'done',
          note: null,
          checkedBy: '',
          checkedAt: new Date().toISOString(),
          optimistic: true,
        })
      );
      try {
        const d = await sendJson('POST', SVM_CHECK_URL, {
          project: projectLabel,
          discipline: disciplineId,
          zone,
          itemNo: item.no,
          col,
          status: 'done',
        });
        pendingRef.current.delete(k);
        mutateChecks((m) => m.set(k, d.check));
      } catch (err) {
        pendingRef.current.delete(k);
        mutateChecks((m) => m.delete(k));
        showToast(err.message || "Couldn't save that tick - try again.");
      }
    },
    [projectLabel, mutateChecks, showToast]
  );

  const doSave = useCallback(
    async (zone, itemNo, col, status, note) => {
      const body = { project: projectLabel, discipline: disciplineId, zone, itemNo, col, status };
      if (note) body.note = note;
      const d = await sendJson('POST', SVM_CHECK_URL, body);
      mutateChecks((m) => m.set(keyOf(zone, itemNo, col), d.check));
    },
    [projectLabel, mutateChecks]
  );

  const doRemove = useCallback(
    async (zone, itemNo, col) => {
      await sendJson('DELETE', SVM_CHECK_URL, { project: projectLabel, discipline: disciplineId, zone, itemNo, col });
      mutateChecks((m) => m.delete(keyOf(zone, itemNo, col)));
    },
    [projectLabel, mutateChecks]
  );

  const doHistory = useCallback((zone, itemNo) => getJson(svmHistoryUrl(projectLabel, disciplineId, zone, itemNo)), [projectLabel]);

  // ── zone scope editing (panel PUTs; throws so the panel shows the error) ──
  const saveZoneScope = useCallback(
    async (zoneEntries) => {
      // zoneEntries = [{ key, robots }, …] straight from the panel.
      const d = await sendJson('PUT', SVM_ZONES_URL, { project: projectLabel, discipline: disciplineId, zones: zoneEntries });
      applyScope(Array.isArray(d.zones) ? d.zones : zoneEntries);
    },
    [projectLabel, applyScope]
  );

  const openScopePanel = useCallback((evt) => {
    const r = evt.currentTarget.getBoundingClientRect();
    setScopePanel({ id: Date.now(), rect: { left: r.left, top: r.top, bottom: r.bottom } });
  }, []);
  const closeScopePanel = useCallback(() => setScopePanel(null), []);

  // ── cell / menu interactions ──
  // The EFFECTIVE zone (robots sliced to scope) - never offers cells beyond
  // this project's robot count for the zone.
  const zoneDef = useMemo(() => scopedZones.find((z) => z.key === activeZone) || null, [scopedZones, activeZone]);

  // If a scope edit shrank the active zone's robots below the current filter
  // (or a zone switch changed the robot set), fall back to ALL.
  const robotFilterSafe = safeRobotFilter(robotFilter, zoneDef);

  const onCellClick = useCallback(
    (item, col, evt) => {
      const k = keyOf(activeZone, item.no, col);
      const existing = checksRef.current.get(k);
      if (!existing) {
        tickDone(activeZone, item, col);
        return;
      }
      if (existing.optimistic) return; // write still in flight
      const r = evt.currentTarget.getBoundingClientRect();
      setPop({
        id: `${k}:${Date.now()}`,
        zone: activeZone,
        item,
        col,
        rect: { left: r.left, top: r.top, bottom: r.bottom },
        initialMode: 'info',
      });
    },
    [activeZone, tickDone]
  );

  const onMenuClick = useCallback(
    (item, evt) => {
      if (!zoneDef) return;
      const r = evt.currentTarget.getBoundingClientRect();
      setPop({
        id: `menu:${activeZone}|${item.no}:${Date.now()}`,
        zone: activeZone,
        item,
        col: null,
        colChoices: colsOf(item, zoneDef.robots),
        rect: { left: r.left, top: r.top, bottom: r.bottom },
        initialMode: 'na',
      });
    },
    [activeZone, zoneDef]
  );

  const closePop = useCallback(() => setPop(null), []);
  const toggleSub = useCallback((key) => {
    setCollapsedSubs((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  // ── derived stats (effective zones: scoped keys, sliced robots) ──
  const zoneStats = useMemo(() => computeZoneStats(scopedZones, checks), [scopedZones, checks]);
  const overall = useMemo(() => computeOverall(zoneStats, scopedZones), [zoneStats, scopedZones]);
  const activeStats = useMemo(() => computeActiveStats(zoneDef, checks), [zoneDef, checks]);
  // Mod-Build vs On-Site split (Controls/Install books only; null for Process).
  const modSite = useMemo(() => computeModSiteStats(zoneDef, checks), [zoneDef, checks]);

  const popCheck = pop && pop.col ? checks.get(keyOf(pop.zone, pop.item.no, pop.col)) : null;

  // ── render ──
  const crumbs = [
    { label: 'Checklists', onClick: onBackToProjects },
    { label: projectLabel, onClick: onBackToDisciplines },
    { label: discipline.label },
  ];
  if (tplError) {
    return (
      <div>
        <Breadcrumb parts={crumbs} />
        <ErrorBanner message={`Couldn't load the checklist template - ${tplError}`} onRetry={fetchTemplate} />
      </div>
    );
  }
  if (!template || !zoneDef) {
    return (
      <div>
        <Breadcrumb parts={crumbs} />
        <LoadingNote text="Loading the SVM template… (first open only)" />
      </div>
    );
  }

  // Robot filter + Edit zones + sync indicator. Desktop: right side of the
  // header row (SyncedAgo first, exactly as before). Narrow: its own wrapping
  // row rendered below the progress bar.
  const headerControls = (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: narrow ? 10 : 12,
        paddingTop: narrow ? 0 : 4,
        flexWrap: narrow ? 'wrap' : 'nowrap',
      }}
    >
      {!narrow && <SyncedAgo lastSync={lastSync} />}
      <select
        value={robotFilterSafe}
        onChange={(e) => setRobotFilter(e.target.value)}
        style={{ ...INPUT, width: 120, fontSize: 11.5, padding: narrow ? '9px 8px' : '6px 8px' }}
        title="Dim other robot columns (visual focus only)"
      >
        <option value="ALL">All robots</option>
        {zoneDef.robots.map((r) => (
          <option key={r} value={r}>
            {r}
          </option>
        ))}
      </select>
      <button
        type="button"
        title="Edit which zones are in this project's build"
        style={{ ...BTN_GHOST, fontSize: 11, padding: narrow ? '10px 12px' : '6px 10px', color: '#777', whiteSpace: 'nowrap' }}
        onClick={openScopePanel}
      >
        Edit zones
      </button>
      {narrow && <SyncedAgo lastSync={lastSync} />}
    </div>
  );

  return (
    <div>
      {/* ── header - narrow stacks: breadcrumb → subtitle → bar → controls ── */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: narrow ? 0 : 260 }}>
          <Breadcrumb parts={crumbs} />
          <div style={{ fontSize: 10, fontWeight: 700, color: GREEN, letterSpacing: 2, textTransform: 'uppercase', marginBottom: 2 }}>
            {template.scope || 'PROCESS ONLY'} · Sealer SVM
          </div>
          <div
            style={{
              fontSize: narrow ? 11 : 10.5,
              color: '#999',
              lineHeight: 1.5,
              maxWidth: 720,
              ...(narrow ? { display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' } : null),
            }}
          >
            {zoneDef.subtitle || ''}
          </div>
        </div>
        {!narrow && headerControls}
      </div>

      <div style={{ margin: '10px 0 4px' }}>
        <ProgressBar signed={overall.signed} total={overall.total} />
      </div>
      {/* Mod-Build vs On-Site: two independent milestones for the active zone.
          "Check all of mod-build" reads 100% here even with site work pending. */}
      {modSite && (
        <div
          style={{ display: 'flex', gap: narrow ? 10 : 18, flexWrap: 'wrap', margin: '2px 0 6px', fontFamily: "'Montserrat', sans-serif" }}
        >
          {[
            ['Mod-Build', modSite.mod, '#6A1B9A'],
            ['On-Site', modSite.site, '#1565C0'],
          ].map(([label, st, color]) => {
            const pct = pctOf(st.s, st.t);
            return (
              <span key={label} style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: narrow ? 11 : 10.5 }}>
                <span style={{ fontWeight: 800, color, letterSpacing: 0.6, textTransform: 'uppercase', fontSize: 9.5 }}>
                  {label} · {activeZone}
                </span>
                <span
                  style={{
                    position: 'relative',
                    width: narrow ? 70 : 90,
                    height: 6,
                    borderRadius: 3,
                    background: '#EEE',
                    overflow: 'hidden',
                    display: 'inline-block',
                  }}
                >
                  <span
                    style={{
                      position: 'absolute',
                      left: 0,
                      top: 0,
                      bottom: 0,
                      width: `${pct}%`,
                      background: pct >= 100 ? GREEN : color,
                      borderRadius: 3,
                    }}
                  />
                </span>
                <span style={{ fontWeight: 800, color: pct >= 100 ? GREEN : '#333', fontVariantNumeric: 'tabular-nums' }}>{pct}%</span>
                <span style={{ color: '#AAA', fontVariantNumeric: 'tabular-nums' }}>
                  ({st.s}/{st.t})
                </span>
              </span>
            );
          })}
        </div>
      )}
      {narrow && <div style={{ margin: '6px 0 8px' }}>{headerControls}</div>}
      <div style={{ fontSize: narrow ? 11 : 10, color: '#AAA', marginBottom: 10 }}>
        Legend: <span style={{ fontWeight: 700, color: '#888' }}>wide grey bar</span> = one sign-off for the whole cell ·{' '}
        <span style={{ fontWeight: 700, color: '#888' }}>squares</span> = one sign-off per robot ({zoneDef.robots.join(', ')})
      </div>

      {checksError && (
        <div
          style={{
            fontSize: 11,
            color: '#B71C1C',
            background: '#FFEBEE',
            border: '1px solid #FFCDD2',
            borderRadius: 8,
            padding: '6px 12px',
            marginBottom: 10,
          }}
        >
          Sync problem: {checksError} - retrying automatically.
        </div>
      )}

      {/* ── zone chips (in-scope zones only, template order) - the ONE
             intentional horizontal scroller on narrow ── */}
      <div
        style={
          narrow
            ? {
                display: 'flex',
                gap: 8,
                flexWrap: 'nowrap',
                overflowX: 'auto',
                WebkitOverflowScrolling: 'touch',
                marginBottom: 14,
                paddingBottom: 4,
              }
            : { display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14 }
        }
      >
        {scopedZones.map((z) => {
          const st = zoneStats[z.key] || { total: 0, signed: 0 };
          const active = z.key === activeZone;
          return (
            <button
              key={z.key}
              type="button"
              onClick={() => setActiveZone(z.key)}
              style={{
                ...BTN,
                padding: narrow ? '10px 14px' : '7px 14px',
                fontSize: 11.5,
                background: active ? GREEN : '#F2F2F2',
                color: active ? '#fff' : '#444',
                border: `1px solid ${active ? GREEN : '#E2E2E2'}`,
                ...(narrow ? { flex: '0 0 auto', whiteSpace: 'nowrap' } : null),
              }}
            >
              {z.key}
              <span
                style={{
                  marginLeft: 6,
                  fontWeight: 600,
                  fontSize: 10,
                  color: active ? 'rgba(255,255,255,0.8)' : '#999',
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {allLoaded || loadedZones.has(z.key) ? `${pctOf(st.signed, st.total)}%` : '…'}
              </span>
            </button>
          );
        })}
      </div>

      {!loadedZones.has(activeZone) && <div style={{ fontSize: 11, color: '#999', marginBottom: 8 }}>Loading checks for {activeZone}…</div>}

      {/* ── product data: equipment serial registry for the active zone ── */}
      <ProductDataPanel projectLabel={projectLabel} disciplineId={disciplineId} zone={activeZone} narrow={narrow} />

      {/* ── phases → subsections → items ── */}
      <div style={{ ...CARD, padding: narrow ? '10px 10px' : '14px 18px' }}>
        {(zoneDef.phases || []).map((ph, pi) => {
          const pStat = activeStats.phases[pi] || { t: 0, s: 0 };
          return (
            <div key={ph.name} style={{ marginBottom: pi < zoneDef.phases.length - 1 ? (narrow ? 14 : 18) : 0 }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: narrow ? 8 : 12,
                  padding: narrow ? '4px 0' : '6px 0',
                  borderBottom: `2px solid ${GREEN}`,
                  marginBottom: narrow ? 8 : 10,
                }}
              >
                <span style={{ fontSize: 12.5, fontWeight: 800, color: GREEN, letterSpacing: 1, textTransform: 'uppercase' }}>
                  {ph.name}
                </span>
                <span style={{ flex: 1 }} />
                <span
                  style={{
                    fontSize: 10.5,
                    fontWeight: 700,
                    color: pStat.s >= pStat.t && pStat.t > 0 ? GREEN : '#999',
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {pStat.s}/{pStat.t} signed
                </span>
              </div>
              {(ph.subsections || []).map((sub) => (
                <SubsectionBlock
                  key={`${ph.name}|${sub.name}`}
                  zoneKey={zoneDef.key}
                  robots={zoneDef.robots}
                  phaseName={ph.name}
                  sub={sub}
                  checks={checks}
                  stat={activeStats.subs[`${ph.name}|${sub.name}`] || { t: 0, s: 0 }}
                  collapsed={collapsedSubs.has(`${ph.name}|${sub.name}`)}
                  onToggle={toggleSub}
                  robotFilter={robotFilterSafe}
                  narrow={narrow}
                  onCellClick={onCellClick}
                  onMenuClick={onMenuClick}
                />
              ))}
            </div>
          );
        })}
      </div>

      {/* ── zone-scope panel + popover + toast ── */}
      {scopePanel && (
        <ZoneScopePanel
          key={scopePanel.id}
          rect={scopePanel.rect}
          zones={template.zones}
          scope={zoneScope}
          onClose={closeScopePanel}
          onSave={saveZoneScope}
        />
      )}
      {pop && (
        <CellPopover key={pop.id} pop={pop} check={popCheck} onClose={closePop} doSave={doSave} doRemove={doRemove} doHistory={doHistory} />
      )}
      {toast && <Toast text={toast} />}
    </div>
  );
}
